/* ============================================================================
   Merchloop ad landing pages.

   Four jobs.

   1. The lead form. It is a real Shopify contact form, so with JavaScript off
      it posts and the lead still arrives. With JavaScript on the submission
      goes to HubSpot's forms submission API, which is a plain fetch rather
      than an embedded iframe: no 300ms re-render to race, and it answers in
      well under a second instead of the nine the embedded form needed.

      That API refuses any form with Captcha turned on, with
      FORM_HAS_RECAPTCHA_ENABLED, so it is not the guaranteed path. Which is
      why delivery and destination are now two separate questions.

      Delivery, in order, first one that answers wins:
        a. HubSpot's submission API.
        b. The Shopify contact form, posted in the background with fetch so
           the page never moves. Same destination as a JavaScript-off post.
        c. The ordinary form post, which navigates.

      Destination, whichever of those delivered: the store request wizard,
      with everything they just typed already in it, landing on step two
      rather than being asked for the same six fields a second time. The
      handoff is the wizard's own draft format, so nothing in the wizard had
      to change to read it. Route (c) reloads this page first, so it leaves a
      marker and boot() carries on to the wizard from the other side.

      Anyone who picks an answer that means "I want to talk to someone" is not
      pushed into the wizard at all. They get the calendar.

      Which fields are asked for, and which are required, is set per section in
      the theme editor. Nothing here hard codes a field list: validation reads
      the required attribute that Liquid emitted.

   2. The booking prefill. The two CTAs are the same ask with two endings, so
      whichever one is pressed the six fields are used. Book a call rewrites the
      scheduler URL with the name, email and company on the way through, which
      HubSpot's meetings pages accept as query parameters, and sends the lead at
      the same time. Nobody types their name twice.

   3. The booking band. The HubSpot meetings embed is around 760px of iframe,
      so it is not loaded until the reader is near it. On a page that exists to
      be fast off a paid click, an iframe nobody has scrolled to yet is the
      most expensive thing that could be on it.

   4. The sticky action bar. Up once the hero has gone past, out of the way
      again over the closing form, gone entirely once the form has been sent.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__mlAd) return;
  window.__mlAd = true;

  var DRAFT_KEY = 'mlsrDraft:v3';
  var HANDOFF_KEY = 'mlaHandoff';
  var RESUME_KEY = 'mlaResume';
  var HS_TIMEOUT = 8000;
  var NATIVE_TIMEOUT = 9000;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[2]) : '';
  }

  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* -- reading the form from anywhere ---------------------------------------
     The sticky bar and the booking band both want what is currently typed into
     the form, and neither is inside it. One reader, used by all of them.
     -------------------------------------------------------------------- */

  function readLead(root) {
    root = root || $('[data-mla-lead]');
    if (!root) return {};
    function v(name) {
      var el = root.querySelector('[data-mla="' + name + '"]');
      return el ? el.value.trim() : '';
    }
    return {
      first: v('first'), last: v('last'), email: v('email'),
      company: v('company'), website: v('website'), phone: v('phone'),
      reason: v('reason')
    };
  }

  /* HubSpot's meetings pages take these four as query parameters and fill the
     booking form with them, which is what makes Book a call the same ask as
     the form rather than a second one. Same four the store request wizard
     already passes from its own help bar. */
  function meetPrefill(url, d) {
    if (!url) return url;
    var add = [];
    if (d.email) add.push('email=' + encodeURIComponent(d.email));
    if (d.first) add.push('firstName=' + encodeURIComponent(d.first));
    if (d.last) add.push('lastName=' + encodeURIComponent(d.last));
    if (d.company) add.push('company=' + encodeURIComponent(d.company));
    if (!add.length) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + add.join('&');
  }

  // A lead sent once is not sent again on the same details, so pressing both
  // buttons, or booking twice, does not file the same person repeatedly.
  var alreadySent = {};

  /* -- the wizard handoff ---------------------------------------------------
     The wizard keeps an unfinished request in localStorage and offers to pick
     it up. Writing one here is what lets the ad page hand over without the
     wizard knowing anything about ad pages. The shape has to match what its
     readDraft expects, including the keys it does not use yet, or the draft is
     thrown away.

     step 2 is the point of the exercise: step 1 is the six fields we just
     collected, so opening there would be asking the same questions twice.
     ---------------------------------------------------------------------- */

  function seedWizardDraft(d) {
    if (!d.company) return false;
    var complete = !!(d.company && d.website && d.first && d.last && d.email);
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        t: Date.now(),
        step: complete ? 2 : 1,
        started: true,
        data: {
          companyName: d.company || '',
          website: d.website || '',
          firstName: d.first || '',
          lastName: d.last || '',
          email: d.email || '',
          phone: d.phone || '',
          logoNotes: '',
          logoSource: '',
          logoFoundUrl: '',
          logoLater: false,
          storeTier: '',
          products: [],
          salesHeadcount: '',
          salesTimeline: '',
          salesNotes: ''
        }
      }));
      sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ t: Date.now() }));
      return true;
    } catch (e) { return false; }
  }

  /* -- HubSpot --------------------------------------------------------------
     The forms submission API rather than the embedded form. Fields that the
     form does not have come back as INVALID_FIELD errors naming each one, so
     a rejection is not a dead end: strip exactly those and send again. That
     means the extra fields we would like to send are sent from the day they
     are added to the form, with no change here.
     ---------------------------------------------------------------------- */

  function hsSubmit(cfg, values) {
    if (!cfg.portal || !cfg.form) return Promise.reject(new Error('hubspot not configured'));
    var url = 'https://api.hsforms.com/submissions/v3/integration/submit/' +
      encodeURIComponent(cfg.portal) + '/' + encodeURIComponent(cfg.form);

    function post(fields) {
      var body = {
        fields: Object.keys(fields)
          .filter(function (k) { return fields[k]; })
          .map(function (k) { return { objectTypeId: '0-1', name: k, value: String(fields[k]) }; }),
        context: {
          pageUri: window.location.href,
          pageName: document.title
        }
      };
      var hutk = cookie('hubspotutk');
      if (hutk) body.context.hutk = hutk;

      var ctrl = window.AbortController ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, HS_TIMEOUT);

      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (r) {
        clearTimeout(timer);
        return r.json().catch(function () { return {}; })
          .then(function (j) { return { status: r.status, body: j }; });
      });
    }

    return post(values).then(function (res) {
      if (res.status === 200) return res;
      var bad = {};
      var errs = (res.body && res.body.errors) || [];
      errs.forEach(function (e) {
        var m = /fields\.([A-Za-z0-9_]+)/.exec((e && e.message) || '');
        if (m) bad[m[1]] = true;
      });
      var names = Object.keys(bad);
      if (!names.length) throw new Error('HubSpot rejected the submission');
      var trimmed = {};
      Object.keys(values).forEach(function (k) { if (!bad[k]) trimmed[k] = values[k]; });
      if (!Object.keys(trimmed).length) throw new Error('HubSpot has none of these fields');
      return post(trimmed).then(function (r2) {
        if (r2.status !== 200) throw new Error('HubSpot rejected the submission');
        return r2;
      });
    });
  }

  /* -- one form ------------------------------------------------------------ */

  function bindLead(root) {
    if (root.__mlaBound) return;
    root.__mlaBound = true;

    var form = $('form', root);
    var done = $('[data-mla-done]', root);
    var btn = $('[data-mla-submit]', root);
    if (!form || !btn) return;

    var cfg = {
      portal: root.getAttribute('data-hs-portal') || '',
      form: root.getAttribute('data-hs-form') || '',
      wizard: root.getAttribute('data-wizard') || '/pages/request-a-store',
      source: root.getAttribute('data-source') || 'ad landing page',
      callValues: (root.getAttribute('data-call-values') || '')
        .split('\n').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean)
    };

    function field(name) { return $('[data-mla="' + name + '"]', form); }

    function flag(el, message) {
      var wrap = el.closest('.mla-f');
      if (!wrap) return;
      wrap.classList.add('is-bad');
      var p = wrap.querySelector('.mla-f__err');
      if (!p) {
        p = document.createElement('p');
        p.className = 'mla-f__err';
        wrap.appendChild(p);
      }
      p.textContent = message;
    }
    function unflag(el) {
      var wrap = el.closest('.mla-f');
      if (!wrap) return;
      wrap.classList.remove('is-bad');
      var p = wrap.querySelector('.mla-f__err');
      if (p) p.remove();
    }
    form.addEventListener('input', function (ev) {
      if (ev.target.matches('input, select')) unflag(ev.target);
    });

    /* Which fields exist, and which of them are required, is a theme editor
       decision. So this reads the required attribute Liquid emitted rather
       than carrying its own list: turn a field off, or make it optional, and
       validation follows without a code change.

       A field that is present and filled is still format checked even when it
       is optional, because a typed email with no @ in it is a lost lead
       whether or not the field was compulsory. */
    var EMPTY_MSG = {
      first: 'Please add your first name',
      last: 'Please add your last name',
      email: 'Please add your work email',
      company: 'Please add your company name',
      website: 'Please add your company website',
      phone: 'Please add a phone number',
      reason: 'Please pick one'
    };
    // Same website test the wizard's own step one uses, so a website that
    // passes here cannot fail there.
    var FORMAT = {
      email: [EMAIL, 'Please enter a valid work email'],
      website: [/^[a-z0-9-]+(\.[a-z0-9-]+)+/i, 'Please enter your website, for example acme.com']
    };

    function validate() {
      var bad = null;
      Object.keys(EMPTY_MSG).forEach(function (key) {
        var el = field(key);
        if (!el || el.disabled) return;
        var val = (el.value || '').trim();
        if (!val) {
          if (el.hasAttribute('required')) { flag(el, EMPTY_MSG[key]); bad = bad || el; }
          return;
        }
        var fmt = FORMAT[key];
        if (!fmt) return;
        var test = key === 'website' ? val.replace(/^https?:\/\//i, '') : val;
        if (!fmt[0].test(test)) { flag(el, fmt[1]); bad = bad || el; }
      });
      if (bad) {
        try { bad.focus({ preventScroll: false }); } catch (e) { bad.focus(); }
      }
      return !bad;
    }

    function collected() { return readLead(root); }

    function wantsCall(reason) {
      var r = (reason || '').trim().toLowerCase();
      return cfg.callValues.indexOf(r) !== -1;
    }

    function summary(d) {
      return [
        'Ad landing page enquiry',
        'Page: ' + cfg.source,
        'Name: ' + (d.first + ' ' + d.last).trim(),
        'Company: ' + d.company,
        'Website: ' + d.website,
        'Email: ' + d.email,
        d.phone ? 'Phone: ' + d.phone : null,
        'What they need: ' + d.reason
      ].filter(Boolean).join('\n');
    }

    function track(d) {
      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'ad_lead_submitted',
            reason: d.reason,
            route: wantsCall(d.reason) ? 'calendar' : 'store request',
            page: cfg.source
          });
        }
        if (window.fbq) window.fbq('track', 'Lead');
      } catch (e) {}
    }

    function hideBar() {
      var bar = $('[data-mla-bar]');
      if (bar) bar.classList.remove('is-up');
      document.body.classList.add('mla-sent');
    }

    // The calendar route. Nothing is gained by sending someone who asked to
    // talk into a six step form, so they get the panel and the page scrolls
    // to the calendar that is already on it.
    function showDone(d) {
      root.classList.add('is-sent');
      hideBar();
      if (done) {
        done.hidden = false;
        done.classList.add('is-shown');
        var h = done.querySelector('h3');
        if (h && d.first) h.textContent = 'Thanks ' + d.first + ', that is with us';
        var body = done.querySelector('[data-mla-done-body]');
        if (body && wantsCall(d.reason)) {
          body.textContent = 'Grab a time below and we will walk you through it. ' +
            'No pitch, just a look at what your store would be.';
        }
      }
      var cal = $('[data-mla-meet]');
      if (wantsCall(d.reason) && cal) {
        loadMeeting(cal);
        setTimeout(function () {
          if (cal.scrollIntoView) cal.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 250);
      } else if (done && done.scrollIntoView) {
        done.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }

    function busy(on) {
      btn.disabled = !!on;
      var label = btn.querySelector('.mla-btn__l');
      if (!label) return;
      if (on) {
        btn.__label = label.textContent;
        label.textContent = 'Sending';
        if (!btn.querySelector('.mla-spin')) {
          var sp = document.createElement('span');
          sp.className = 'mla-spin';
          btn.insertBefore(sp, btn.firstChild);
        }
      } else {
        if (btn.__label) label.textContent = btn.__label;
        var old = btn.querySelector('.mla-spin');
        if (old) old.remove();
      }
    }

    form.addEventListener('submit', function (ev) {
      if (form.__mlaGoNative) return;             // the fallback's own submit
      ev.preventDefault();
      if (!validate()) return;

      var d = collected();
      var body = summary(d);

      // Fill the hidden fields the native post relies on, so the fallback
      // carries everything the HubSpot path would have.
      var nameEl = field('name');
      if (nameEl) nameEl.value = (d.first + ' ' + d.last).trim();
      var bodyEl = field('body');
      if (bodyEl) bodyEl.value = body;

      busy(true);

      // Route (c). The ordinary form post, which navigates. The marker is
      // what makes the reload carry on to the wizard rather than dead ending
      // on a success panel with a button to press.
      function goNative() {
        form.__mlaGoNative = true;
        track(d);
        seedWizardDraft(d);
        try {
          sessionStorage.setItem(RESUME_KEY, JSON.stringify({
            t: Date.now(),
            wizard: cfg.wizard,
            call: wantsCall(d.reason) ? 1 : 0
          }));
        } catch (e) {}
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }

      // Route (b). The same post route (c) takes, sent with fetch so the page
      // stays where it is and we keep control of where the visitor goes next.
      function postNative() {
        var ctrl = window.AbortController ? new AbortController() : null;
        var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, NATIVE_TIMEOUT);
        return fetch(form.getAttribute('action') || window.location.pathname, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          signal: ctrl ? ctrl.signal : undefined
        }).then(function (r) {
          clearTimeout(timer);
          if (!r.ok) throw new Error('contact form post returned ' + r.status);
          return r;
        });
      }

      function handoff() {
        // The marker tells the wizard page to open the saved draft rather than
        // offering it, so the visitor never sees a "pick up where you left
        // off" prompt for something they filled in eight seconds ago.
        window.location.href = cfg.wizard + (cfg.wizard.indexOf('?') === -1 ? '?' : '&') + 'mlad=1';
      }

      hsSubmit(cfg, {
        email: d.email,
        firstname: d.first,
        lastname: d.last,
        company: d.company,
        website: d.website,
        phone: d.phone,
        lead_source_detail: 'Ad landing page: ' + cfg.source,
        message: body
      }).then(function () {
        alreadySent[d.email.toLowerCase()] = true;
        return 'hubspot';
      }).catch(function (e) {
        if (window.console) console.warn('[ml-ad] HubSpot send failed, posting the contact form instead', e);
        return postNative().then(function () { return 'contact form'; });
      }).then(function (via) {
        if (window.console) console.info('[ml-ad] lead delivered by ' + via);
        track(d);
        seedWizardDraft(d);
        if (wantsCall(d.reason)) {
          busy(false);
          showDone(d);
          return;
        }
        handoff();
      }).catch(function (e) {
        // Both quiet routes are gone. The ordinary post cannot be blocked by
        // anything a fetch can be blocked by, so it is the one that always
        // works, at the cost of a page load.
        if (window.console) console.warn('[ml-ad] background post failed, submitting the form for real', e);
        goNative();
      });
    });
  }

  /* -- the booking buttons --------------------------------------------------
     Every scheduler link on an ad page carries data-mla-book. This listens in
     the capture phase, which puts it ahead of ml-meet.js's own document
     handler, and rewrites the href with whatever is currently in the form.
     It never calls preventDefault, so ml-meet.js still opens the dialog from
     the URL it finds, and with JavaScript off the link is simply the plain
     scheduler.

     A booking is a lead too, so if there is a usable email it goes to HubSpot
     on the way past. That send is deliberately not waited on: the dialog opens
     immediately either way, and a booking must never be held up by a form post.
     -------------------------------------------------------------------- */

  function bindBookLinks() {
    if (document.__mlaBook) return;
    document.__mlaBook = true;

    document.addEventListener('click', function (ev) {
      var a = ev.target.closest && ev.target.closest('[data-mla-book]');
      if (!a) return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button > 0) return;

      var root = a.closest('[data-mla-lead]') || $('[data-mla-lead]');
      var d = readLead(root);

      var base = a.getAttribute('data-mla-base');
      if (!base) {
        base = a.getAttribute('href') || '';
        a.setAttribute('data-mla-base', base);
      }
      a.setAttribute('href', meetPrefill(base, d));

      if (!root || !EMAIL.test(d.email || '')) return;
      if (alreadySent[d.email.toLowerCase()]) return;
      alreadySent[d.email.toLowerCase()] = true;

      var cfg = {
        portal: root.getAttribute('data-hs-portal') || '',
        form: root.getAttribute('data-hs-form') || '',
        source: root.getAttribute('data-source') || 'ad landing page'
      };
      var body = [
        'Ad landing page enquiry, booked a call',
        'Page: ' + cfg.source,
        'Name: ' + (d.first + ' ' + d.last).trim(),
        'Company: ' + d.company,
        'Website: ' + d.website,
        'Email: ' + d.email,
        d.phone ? 'Phone: ' + d.phone : null,
        d.reason ? 'What they need: ' + d.reason : null
      ].filter(Boolean).join('\n');

      hsSubmit(cfg, {
        email: d.email,
        firstname: d.first,
        lastname: d.last,
        company: d.company,
        website: d.website,
        phone: d.phone,
        lead_source_detail: 'Ad landing page, booking: ' + cfg.source,
        message: body
      }).catch(function (e) {
        // The booking itself is unaffected, so this is a warning and not an
        // error path. The dialog is already open by now.
        if (window.console) console.warn('[ml-ad] booking lead not sent', e);
      });

      seedWizardDraft(d);
      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'ad_lead_submitted', reason: d.reason,
            route: 'calendar', page: cfg.source
          });
        }
        if (window.fbq) window.fbq('track', 'Lead');
      } catch (e) {}
    }, true);
  }

  /* -- the booking band ---------------------------------------------------- */

  function loadMeeting(host) {
    if (!host || host.__loaded) return;
    host.__loaded = true;
    // Prefill at load rather than at render: the embed is not fetched until
    // the reader is near it, which is well after they have had the chance to
    // fill the form above. Someone who filled it and scrolled down gets a
    // calendar that already knows who they are.
    var d = readLead();
    var base = host.getAttribute('data-src') || '';
    var next = meetPrefill(base, d);
    if (next !== base) host.setAttribute('data-src', next);
    var s = document.createElement('script');
    s.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    s.async = true;
    document.body.appendChild(s);
  }

  function bindMeeting() {
    var host = $('[data-mla-meet]');
    if (!host || host.__mlaBound) return;
    host.__mlaBound = true;

    if (!('IntersectionObserver' in window)) return loadMeeting(host);
    new IntersectionObserver(function (rows, ob) {
      if (!rows[0].isIntersecting) return;
      loadMeeting(host);
      ob.disconnect();
    }, { rootMargin: '500px 0px' }).observe(host);
  }

  /* -- the sticky bar ------------------------------------------------------ */

  function bindBar() {
    var bar = $('[data-mla-bar]');
    if (!bar || bar.__mlaBound) return;
    bar.__mlaBound = true;

    var hero = $('[data-mla-hero]');
    var closer = $('[data-mla-closer]');

    bar.addEventListener('click', function (ev) {
      var jump = ev.target.closest('[data-mla-jump]');
      if (!jump) return;
      ev.preventDefault();
      var target = $('[data-mla-lead]');
      if (!target) return;
      if (target.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var first = target.querySelector('input, select');
      if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (e) {} }, 420);
    });

    // Two observers rather than a scroll listener: the bar is up when the hero
    // is behind you and the closing band is not yet in front of you.
    var past = false, atCloser = false;
    function paint() {
      bar.classList.toggle('is-up', past && !atCloser && !document.body.classList.contains('mla-sent'));
    }
    if (!('IntersectionObserver' in window)) { bar.classList.add('is-up'); return; }

    if (hero) {
      new IntersectionObserver(function (rows) {
        past = !rows[0].isIntersecting;
        paint();
      }, { rootMargin: '-40px 0px 0px 0px' }).observe(hero);
    } else {
      past = true;
    }
    if (closer) {
      new IntersectionObserver(function (rows) {
        atCloser = rows[0].isIntersecting;
        paint();
      }, { rootMargin: '0px 0px -120px 0px' }).observe(closer);
    }
    paint();
  }

  /* -- boot ---------------------------------------------------------------- */

  /* Coming back from the ordinary form post. The lead is in, so the only
     question left is where the visitor goes, and the answer is the same one
     the quiet routes give: the store request, carrying on from the details
     they just typed.

     The marker is required, and it expires, so someone who lands on this page
     with contact_posted on the URL for any other reason is left alone. */
  function resumeAfterNativePost() {
    if (!/[?&]contact_posted=true/.test(window.location.search)) return false;
    var m = null;
    try {
      m = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null');
      sessionStorage.removeItem(RESUME_KEY);
    } catch (e) {}
    if (!m || !m.wizard || !m.t || Date.now() - m.t > 10 * 60 * 1000) return false;
    if (m.call) return false;
    window.location.replace(m.wizard + (m.wizard.indexOf('?') === -1 ? '?' : '&') + 'mlad=1');
    return true;
  }

  function boot() {
    // Before anything is bound or painted, so the page does not flash a
    // success panel the visitor is about to be moved off.
    if (resumeAfterNativePost()) return;
    $$('[data-mla-lead]').forEach(bindLead);
    bindBookLinks();
    bindMeeting();
    bindBar();
    // Coming back from a native post: the ask is answered, so the bar stays
    // down and the page does not keep selling.
    if (/[?&]contact_posted=true/.test(window.location.search)) {
      document.body.classList.add('mla-sent');
      var bar = $('[data-mla-bar]');
      if (bar) bar.classList.remove('is-up');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
